import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, BadgeDollarSign, CalendarClock, ClipboardCheck,
  Leaf, MapPin, Sparkles, Building2, Home as HomeIcon,
} from "lucide-react";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLine } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { QuoteForm } from "../components/QuoteForm";

const HERO_IMG = "https://images.unsplash.com/photo-1649083048770-82e8ffd80431?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHxfYXJ3cmRzX2hlcm9fM3w&q=85";
const HERO_IMG_2 = "https://images.unsplash.com/photo-1631510390389-c1e4fb20ff31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxoZXJvX2xpdmluZ3Jvb218&q=85";

const BENEFITS = [
  { icon: BadgeDollarSign, title: "Fair, tailored pricing", text: "Every quote is built around your space, frequency, and priorities - never a one-size-fits-all rate. No hidden fees, ever.", span: "md:col-span-2", dark: true },
  { icon: ClipboardCheck, title: "Detail-first checklists", text: "Every visit follows the same thorough, room-by-room checklist, so the result is consistent - not lucky.", span: "", dark: false },
  { icon: CalendarClock, title: "Scheduling around you", text: "Early mornings, evenings, weekends, or after-hours for businesses. Your clean fits your calendar.", span: "", dark: false },
  { icon: MapPin, title: "Local & accountable", text: "Based in the greater St. Louis area. You talk to the people who clean your space, not a call center.", span: "", dark: false },
  { icon: Leaf, title: "Greener product options", text: "Ask about our lower-impact product choices for homes with kids, pets, and sensitivities.", span: "", dark: false },
];

const STEPS = [
  { n: "01", title: "Tell us about your space", text: "Two minutes in the quote form: property type, size, frequency, and anything that matters to you." },
  { n: "02", title: "Get a tailored quote", text: "We respond personally with a clear, written estimate. Everything included, nothing hidden." },
  { n: "03", title: "Enjoy the clean", text: "A consistent team follows a detailed checklist on every visit - you just come home to the glow." },
];

export default function Home() {
  const [services, setServices] = useState([]);
  const [areas, setAreas] = useState([]);
  const [settings, setSettings] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/areas").then((r) => setAreas(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <Seo
        title="Glow Green | Commercial & Residential Cleaning in St. Louis & Chesterfield, MO"
        description="A brighter clean. A better value. Premium commercial and residential cleaning in St. Louis, Chesterfield, and surrounding Missouri communities. Request a free quote."
      />

      {/* HERO */}
      <section ref={heroRef} className="relative bg-forest grain overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-32 lg:pt-44 pb-20 lg:pb-28 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <motion.div className="lg:col-span-7" style={{ y: textY }}>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="uppercase text-xs font-semibold tracking-[0.25em] text-leaf font-mono"
              >
                St. Louis · Chesterfield · Missouri
              </motion.p>
              <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight text-white leading-[1.05]">
                <MaskedLine delay={0.3}>A Brighter Clean.</MaskedLine>
                <MaskedLine delay={0.45} className="text-leaf">A Better Value.</MaskedLine>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.7 }}
                className="mt-6 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed"
              >
                Glow Green brings premium commercial and residential cleaning to the greater St. Louis area - detailed checklists, flexible scheduling, and honest quotes tailored to your space.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85, duration: 0.7 }}
                className="mt-8 flex flex-wrap items-center gap-3"
              >
                <a href="#quote" data-testid="hero-quote-button" className="inline-flex items-center gap-2 rounded-full bg-leaf px-7 py-4 font-display font-bold text-sm text-forest-deep transition-all hover:bg-mint hover:scale-[1.03]">
                  Request a Free Quote <ArrowRight className="w-4 h-4" />
                </a>
                <Link to="/commercial-cleaning" data-testid="hero-commercial-button" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-4 font-display font-bold text-sm text-white transition-colors hover:border-leaf hover:text-leaf">
                  <Building2 className="w-4 h-4" /> Commercial
                </Link>
                <Link to="/residential-cleaning" data-testid="hero-residential-button" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-4 font-display font-bold text-sm text-white transition-colors hover:border-leaf hover:text-leaf">
                  <HomeIcon className="w-4 h-4" /> Residential
                </Link>
              </motion.div>
            </motion.div>

            <div className="lg:col-span-5 relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
              >
                <div className="absolute -inset-3 rounded-[2rem] border border-leaf/30 translate-x-4 translate-y-4" aria-hidden="true" />
                <div className="relative overflow-hidden rounded-[1.75rem] clipped-frame aspect-[4/5]">
                  <motion.img
                    src={HERO_IMG}
                    alt="Bright sunlit clean living room with modern fireplace and hardwood floor"
                    className="w-full h-[115%] object-cover"
                    style={{ y: imgY }}
                    fetchPriority="high"
                  />
                </div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.05, duration: 0.7 }}
                  className="absolute -bottom-6 -left-4 sm:-left-8 rounded-2xl bg-white/95 backdrop-blur px-5 py-4 shadow-2xl flex items-center gap-3"
                >
                  <span className="w-10 h-10 rounded-full bg-mint flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-leaf-hover" />
                  </span>
                  <div>
                    <p className="font-display font-bold text-forest text-sm">Free custom quotes</p>
                    <p className="text-xs text-slate-500">No hidden fees. Ever.</p>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* SERVICES */}
      <section className="py-20 lg:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <p className="uppercase text-xs font-semibold tracking-[0.2em] text-leaf-hover">What we do</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-forest max-w-2xl">
              Two crafts. One standard of clean.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {services.map((svc, i) => (
              <Reveal key={svc.id} delay={i * 0.1}>
                <Link
                  to={svc.category === "commercial" ? "/commercial-cleaning" : "/residential-cleaning"}
                  data-testid={`service-card-${svc.category}`}
                  className="group block rounded-2xl overflow-hidden bg-white border border-forest/10 hover:border-leaf/40 hover:shadow-2xl hover:shadow-leaf/10 transition-all h-full"
                >
                  <div className="overflow-hidden aspect-[16/9] relative">
                    <img src={svc.image} alt={svc.image_alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute top-4 left-4 rounded-full bg-forest/85 backdrop-blur px-4 py-1.5 text-xs font-bold text-white uppercase tracking-wider">{svc.category}</span>
                  </div>
                  <div className="p-6 lg:p-8">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-xl sm:text-2xl font-semibold text-forest">{svc.title}</h3>
                      <ArrowUpRight className="w-5 h-5 text-leaf-hover transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </div>
                    <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">{svc.summary}</p>
                    <ul className="mt-4 flex flex-wrap gap-2">
                      {svc.items.slice(0, 5).map((item) => (
                        <li key={item} className="rounded-full bg-mint/60 px-3 py-1 text-xs font-medium text-forest">{item}</li>
                      ))}
                      {svc.items.length > 5 && (
                        <li className="rounded-full border border-forest/15 px-3 py-1 text-xs font-medium text-slate-500">+{svc.items.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS BENTO */}
      <section className="py-20 lg:py-32 bg-forest grain relative" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative z-10">
          <Reveal>
            <p className="uppercase text-xs font-semibold tracking-[0.2em] text-leaf">Why Glow Green</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-2xl">
              Premium where it matters. Practical where it counts.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.07} className={b.span}>
                <div className={`h-full rounded-2xl p-6 lg:p-8 border transition-all hover:-translate-y-1 ${b.dark ? "bg-leaf text-forest-deep border-leaf" : "bg-forest-soft/60 text-white border-white/10 hover:border-leaf/40"}`}>
                  <b.icon className={`w-7 h-7 ${b.dark ? "text-forest" : "text-leaf"}`} />
                  <h3 className="mt-4 font-display text-lg font-bold">{b.title}</h3>
                  <p className={`mt-2 text-sm leading-relaxed ${b.dark ? "text-forest/80" : "text-white/65"}`}>{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 lg:py-32" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <Reveal className="lg:col-span-5">
              <p className="uppercase text-xs font-semibold tracking-[0.2em] text-leaf-hover">How it works</p>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-forest">
                From first hello to final gleam.
              </h2>
              <div className="mt-8 overflow-hidden rounded-2xl clipped-frame aspect-[4/3]">
                <img src={HERO_IMG_2} alt="White sofa near bright sunlit window with green houseplant in a freshly cleaned living room" loading="lazy" className="w-full h-full object-cover" />
              </div>
            </Reveal>
            <div className="lg:col-span-7 space-y-6 lg:pt-16">
              {STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.1}>
                  <div className="flex gap-6 items-start rounded-2xl bg-white border border-forest/10 p-6 lg:p-8 hover:border-leaf/40 transition-colors">
                    <span className="font-display font-extrabold text-3xl text-leaf/80">{s.n}</span>
                    <div>
                      <h3 className="font-display text-lg font-bold text-forest">{s.title}</h3>
                      <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VALUE / PRICING */}
      <section className="py-20 lg:py-28 bg-mint/40" data-testid="value-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <p className="uppercase text-xs font-semibold tracking-[0.2em] text-leaf-hover">Pricing</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-forest max-w-2xl">
              Priced to be the better value.
            </h2>
            <p className="mt-4 max-w-2xl text-slate-600 leading-relaxed">
              We aim to price slightly below comparable local quotes while keeping quality high - so you get more clean for your budget. Because every space is different, every estimate is tailored: scope, frequency, square footage, and priorities, all itemized in writing before we start.
            </p>
          </Reveal>
          {settings?.pricing_enabled && settings?.pricing_items?.length > 0 && (
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="pricing-grid">
              {settings.pricing_items.map((item, i) => (
                <Reveal key={i} delay={i * 0.07}>
                  <div className="rounded-2xl bg-white border border-forest/10 p-6 lg:p-8 h-full">
                    <h3 className="font-display text-lg font-bold text-forest">{item.name}</h3>
                    <p className="mt-2 font-display text-2xl font-extrabold text-leaf-hover">{item.price}</p>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{item.description}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="py-20 lg:py-28" data-testid="areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="uppercase text-xs font-semibold tracking-[0.2em] text-leaf-hover">Service areas</p>
                <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-forest">
                  Local to the greater St. Louis area.
                </h2>
              </div>
              <Link to="/service-areas" data-testid="areas-view-all-link" className="inline-flex items-center gap-2 text-sm font-bold text-leaf-hover hover:gap-3 transition-all">
                View all areas <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {areas.map((area, i) => (
              <Reveal key={area.id} delay={i * 0.07}>
                <div className="rounded-2xl bg-white border border-forest/10 p-6 hover:border-leaf/40 hover:shadow-lg transition-all h-full">
                  <MapPin className="w-5 h-5 text-leaf" />
                  <h3 className="mt-3 font-display text-lg font-bold text-forest">{area.name}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{area.blurb}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section id="quote" className="py-20 lg:py-32 bg-forest grain relative" data-testid="quote-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-28">
            <p className="uppercase text-xs font-semibold tracking-[0.2em] text-leaf">Free quote</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Let's make your space glow.
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed max-w-md">
              Tell us about your home or business and we'll build a custom estimate around your space, schedule, and priorities. No obligation, no hidden fees.
            </p>
            <ul className="mt-8 space-y-3">
              {["Tailored written estimate", "Flexible scheduling", "Detailed cleaning checklist"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm text-white/80">
                  <Sparkles className="w-4 h-4 text-leaf flex-shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <QuoteForm dark />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
